import { useEffect, useState, type FormEvent } from 'react';
import { loadReferences } from '../lib/data';
import { errorMessage } from '../lib/errors';
import { supabase } from '../lib/supabase';
import { slugify } from '../lib/text';
import type { Category, ContentPack, Language } from '../lib/types';

const ICON_KEYS = [
  'movies_series',
  'art',
  'video_games',
  'music',
  'history',
  'science',
  'geography',
  'animals_nature',
  'sports',
  'famous_people',
] as const;

type CategoryDraft = Omit<Category, 'description' | 'icon_key'> & {
  description: string;
  icon_key: string;
};

const EMPTY_CATEGORY: CategoryDraft = {
  id: '',
  label: '',
  description: '',
  icon_key: '',
  sort_order: 100,
  is_active: true,
};

export function CataloguesPage() {
  const [languages, setLanguages] = useState<Language[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [packs, setPacks] = useState<ContentPack[]>([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [newLanguage, setNewLanguage] = useState({ code: '', label: '' });
  const [categoryDraft, setCategoryDraft] = useState<CategoryDraft>(EMPTY_CATEGORY);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [newPack, setNewPack] = useState({ id: '', name: '', language_code: 'fr', description: '' });

  const reload = async () => {
    const refs = await loadReferences();
    setLanguages(refs.languages);
    setCategories(refs.categories);
    setPacks(refs.packs);
    if (!newPack.language_code && refs.languages[0]) {
      setNewPack((current) => ({ ...current, language_code: refs.languages[0].code }));
    }
  };

  useEffect(() => {
    void reload().catch((caught) => setError(errorMessage(caught)));
  }, []);

  const execute = async (operation: () => Promise<void>, message: string) => {
    setError('');
    setSuccess('');
    try {
      await operation();
      await reload();
      setSuccess(message);
    } catch (caught) {
      setError(errorMessage(caught));
    }
  };

  const addLanguage = (event: FormEvent) => {
    event.preventDefault();
    void execute(async () => {
      const { error: queryError } = await supabase.from('languages').insert({
        code: newLanguage.code.trim(),
        label: newLanguage.label.trim(),
      });
      if (queryError) throw queryError;
      setNewLanguage({ code: '', label: '' });
    }, 'Langue créée.');
  };

  const saveCategory = (event: FormEvent) => {
    event.preventDefault();
    void execute(async () => {
      const payload: Category = {
        id: categoryDraft.id.trim(),
        label: categoryDraft.label.trim(),
        description: categoryDraft.description.trim() || null,
        icon_key: categoryDraft.icon_key || null,
        sort_order: categoryDraft.sort_order,
        is_active: categoryDraft.is_active,
      };
      const query = editingCategoryId
        ? supabase.from('categories').update(payload).eq('id', editingCategoryId)
        : supabase.from('categories').insert(payload);
      const { error: queryError } = await query;
      if (queryError) throw queryError;
      setCategoryDraft(EMPTY_CATEGORY);
      setEditingCategoryId(null);
    }, editingCategoryId ? 'Catégorie mise à jour.' : 'Catégorie créée.');
  };

  const editCategory = (category: Category) => {
    setEditingCategoryId(category.id);
    setCategoryDraft({
      ...category,
      description: category.description ?? '',
      icon_key: category.icon_key ?? '',
    });
  };

  const addPack = (event: FormEvent) => {
    event.preventDefault();
    void execute(async () => {
      const { error: queryError } = await supabase.from('content_packs').insert({
        id: newPack.id.trim(),
        name: newPack.name.trim(),
        language_code: newPack.language_code,
        description: newPack.description.trim() || null,
        status: 'draft',
      });
      if (queryError) throw queryError;
      setNewPack({ id: '', name: '', language_code: languages[0]?.code ?? 'fr', description: '' });
    }, 'Pack créé.');
  };

  const toggleLanguage = (
    code: string,
    enabled: boolean,
  ) => {
    void execute(async () => {
      const { error: queryError } = await supabase.from('languages').update({ enabled }).eq('code', code);
      if (queryError) throw queryError;
    }, 'Référentiel mis à jour.');
  };

  const updatePackStatus = (id: string, status: ContentPack['status']) => {
    void execute(async () => {
      const { error: queryError } = await supabase.from('content_packs').update({ status }).eq('id', id);
      if (queryError) throw queryError;
    }, 'Pack mis à jour.');
  };

  return (
    <div className="page-stack">
      <header className="page-header">
        <div>
          <p className="eyebrow">Configuration éditoriale</p>
          <h1>Référentiels</h1>
          <p>Langues, catégories et packs disponibles dans les formulaires et les imports.</p>
        </div>
      </header>

      {error && <div className="alert error">{error}</div>}
      {success && <div className="alert success">{success}</div>}

      <section className="catalogue-grid">
        <article className="panel">
          <div className="panel-heading"><div><p className="eyebrow">Langues</p><h2>{languages.length} langue(s)</h2></div></div>
          <form className="inline-create-form" onSubmit={addLanguage}>
            <input required maxLength={5} placeholder="fr" value={newLanguage.code} onChange={(event) => setNewLanguage({ ...newLanguage, code: event.target.value })} />
            <input required placeholder="Français" value={newLanguage.label} onChange={(event) => setNewLanguage({ ...newLanguage, label: event.target.value })} />
            <button className="button primary small">Ajouter</button>
          </form>
          <div className="simple-list">
            {languages.map((language) => (
              <div key={language.code}>
                <div><strong>{language.label}</strong><span>{language.code}</span></div>
                <button className="button ghost small" onClick={() => toggleLanguage(language.code, !language.enabled)}>
                  {language.enabled ? 'Désactiver' : 'Activer'}
                </button>
              </div>
            ))}
          </div>
        </article>

        <article className="panel">
          <div className="panel-heading"><div><p className="eyebrow">Catégories</p><h2>{categories.length} catégorie(s)</h2></div></div>
          <form className="pack-create-form" onSubmit={saveCategory}>
            <label>Identifiant<input required pattern="[a-z0-9_]+" placeholder="science" value={categoryDraft.id} onChange={(event) => setCategoryDraft({ ...categoryDraft, id: event.target.value.trim().toLowerCase() })} /></label>
            <label>Titre<input required placeholder="Sciences" value={categoryDraft.label} onChange={(event) => setCategoryDraft({ ...categoryDraft, label: event.target.value })} /></label>
            <label>Icône<select value={categoryDraft.icon_key} onChange={(event) => setCategoryDraft({ ...categoryDraft, icon_key: event.target.value })}><option value="">Aucune</option>{ICON_KEYS.map((key) => <option key={key} value={key}>{key}</option>)}</select></label>
            <label>Ordre<input required type="number" value={categoryDraft.sort_order} onChange={(event) => setCategoryDraft({ ...categoryDraft, sort_order: Number(event.target.value) })} /></label>
            <label className="span-2">Description<textarea value={categoryDraft.description} onChange={(event) => setCategoryDraft({ ...categoryDraft, description: event.target.value })} /></label>
            <label className="checkbox-field"><input type="checkbox" checked={categoryDraft.is_active} onChange={(event) => setCategoryDraft({ ...categoryDraft, is_active: event.target.checked })} /> Active</label>
            <div className="header-actions">
              <button className="button primary small">{editingCategoryId ? 'Enregistrer' : 'Ajouter'}</button>
              {editingCategoryId && <button type="button" className="button ghost small" onClick={() => { setCategoryDraft(EMPTY_CATEGORY); setEditingCategoryId(null); }}>Annuler</button>}
            </div>
          </form>
          <div className="simple-list">
            {categories.map((category) => (
              <div key={category.id}>
                <div><strong>{category.label}</strong><span>{category.id} · ordre {category.sort_order} · {category.icon_key || 'sans icône'} · {category.is_active ? 'active' : 'inactive'}</span>{category.description && <span>{category.description}</span>}</div>
                <button className="button ghost small" onClick={() => editCategory(category)}>Modifier</button>
              </div>
            ))}
          </div>
        </article>
      </section>

      <section className="panel">
        <div className="panel-heading"><div><p className="eyebrow">Packs de contenu</p><h2>{packs.length} pack(s)</h2></div></div>
        <form className="pack-create-form" onSubmit={addPack}>
          <label>
            Identifiant
            <input required placeholder="base_fr_prod_001" value={newPack.id} onChange={(event) => setNewPack({ ...newPack, id: event.target.value.trim().toLowerCase() })} />
          </label>
          <label>
            Nom
            <input required placeholder="Base française" value={newPack.name} onChange={(event) => {
              const name = event.target.value;
              setNewPack((current) => ({
                ...current,
                name,
                id: current.id || slugify(name),
              }));
            }} />
          </label>
          <label>
            Langue
            <select value={newPack.language_code} onChange={(event) => setNewPack({ ...newPack, language_code: event.target.value })}>
              {languages.map((language) => <option key={language.code} value={language.code}>{language.label}</option>)}
            </select>
          </label>
          <label className="span-2">
            Description
            <input value={newPack.description} onChange={(event) => setNewPack({ ...newPack, description: event.target.value })} />
          </label>
          <button className="button primary">Créer le pack</button>
        </form>

        <div className="table-scroll top-gap">
          <table>
            <thead><tr><th>Pack</th><th>Langue</th><th>Description</th><th>Statut</th></tr></thead>
            <tbody>
              {packs.length === 0 ? (
                <tr><td className="empty-cell" colSpan={4}>Crée au moins un pack avant l’import.</td></tr>
              ) : packs.map((pack) => (
                <tr key={pack.id}>
                  <td><strong>{pack.name}</strong><span className="row-subtitle">{pack.id}</span></td>
                  <td>{pack.language_code}</td>
                  <td>{pack.description || '—'}</td>
                  <td>
                    <select value={pack.status} onChange={(event) => updatePackStatus(pack.id, event.target.value as ContentPack['status'])}>
                      <option value="draft">draft</option>
                      <option value="active">active</option>
                      <option value="disabled">disabled</option>
                      <option value="archived">archived</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
